/**
 * InsightPro™ — Sales & Revenue Page  v1.0
 *
 * Full drill-down hierarchy:
 *   Overview → Month detail → Branch detail → Dept detail → Product detail
 *
 * All data from Power BI via pbiQuery using confirmed DAX queries.
 * Breadcrumb navigation, spinner per panel, no page reloads.
 */
import React, { useState, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { pbiQuery } from '../utils/api';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth < 768);
  React.useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);
  return isMobile;
}

// ── Period → date range ───────────────────────────────────────
function getDateRange(period) {
  const now   = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  const dow = today.getDay();

  let startDate, endDate;

  if (period === 'TODAY') {
    startDate = `${y},${m},${d}`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'YESTERDAY' || period === 'YEST') {
    const yest = new Date(today);
    yest.setDate(yest.getDate() - 1);
    const yy = yest.getFullYear(), ym = yest.getMonth() + 1, yd = yest.getDate();
    startDate = `${yy},${ym},${yd}`;
    endDate   = `${yy},${ym},${yd}`;
  } else if (period === 'WTD') {
    const start = new Date(today);
    start.setDate(today.getDate() - (dow === 0 ? 6 : dow - 1));
    startDate = `${start.getFullYear()},${start.getMonth()+1},${start.getDate()}`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'MTD') {
    startDate = `${y},${m},1`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'QTD') {
    const qStart = Math.floor((m - 1) / 3) * 3 + 1;
    startDate = `${y},${qStart},1`;
    endDate   = `${y},${m},${d}`;
  } else if (period === 'YTD') {
    startDate = `${y},1,1`;
    endDate   = `${y},${m},${d}`;
  } else {
    startDate = `${y},${m},1`;
    endDate   = `${y},${m},${d}`;
  }

  // Prior year same period
  const [sy, sm, sd] = startDate.split(',').map(Number);
  const [ey, em, ed] = endDate.split(',').map(Number);
  const pyStart = `${sy-1},${sm},${sd}`;
  const pyEnd   = `${ey-1},${em},${ed}`;

  return { startDate, endDate, pyStart, pyEnd };
}

// Build DAX with explicit DATESBETWEEN for current + prior year
function periodDax(period, innerDax) {
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  return innerDax
    .replace(/__CY_START__/g, startDate)
    .replace(/__CY_END__/g, endDate)
    .replace(/__PY_START__/g, pyStart)
    .replace(/__PY_END__/g, pyEnd);
}

// ── Formatters ────────────────────────────────────────────────
const fmtR = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `R ${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000)     return `R ${(v / 1_000).toFixed(0)}K`;
  return `R ${v.toFixed(0)}`;
};
const fmtPct = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n) * 100;
  return `${v > 0 ? '+' : ''}${v.toFixed(1)}%`;
};
const fmtN = (n) => n == null ? '—' : Number(n).toLocaleString('en-ZA');
const GOLD = '#c4922a';
const GREEN = '#1d9e75';
const RED = '#c0392b';

// ── usePbi hook — runs a raw DAX query ────────────────────────
function usePbi(dax, deps = []) {
  const [data,    setData]    = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error,   setError]   = React.useState(null);

  const load = useCallback(async () => {
    if (!dax) return;
    setLoading(true); setError(null);
    try {
      const r = await pbiQuery({ dax });
      setData(r?.rows || []);
    } catch(e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [dax]); // eslint-disable-line

  React.useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// ── Shared UI components ──────────────────────────────────────
function Spinner() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'48px 0' }}>
      <div style={{
        width:32, height:32, border:`3px solid rgba(196,146,42,0.2)`,
        borderTop:`3px solid ${GOLD}`, borderRadius:'50%',
        animation:'spin 0.8s linear infinite'
      }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

function ErrorBox({ msg, onRetry }) {
  return (
    <div style={{ padding:'24px', textAlign:'center' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--red)', letterSpacing:'0.12em', marginBottom:8 }}>DATA LOAD ERROR</div>
      <div style={{ fontSize:13, color:'var(--page-muted)', marginBottom:16 }}>{msg}</div>
      {onRetry && <button onClick={onRetry} style={{ fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em', padding:'7px 16px', background:'none', border:'1px solid var(--gold)', color:'var(--gold)', cursor:'pointer', borderRadius:2 }}>RETRY</button>}
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, ...style }}>
      {children}
    </div>
  );
}

function CardHeader({ title, subtitle }) {
  return (
    <div style={{ padding:'16px 20px 0', borderBottom:'1px solid var(--card-border)', paddingBottom:12, marginBottom:16 }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)' }}>{title}</div>
      {subtitle && <div style={{ fontSize:12, color:'var(--page-muted)', marginTop:3 }}>{subtitle}</div>}
    </div>
  );
}

function KpiCard({ label, value, sub, subColor }) {
  return (
    <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, padding:'16px 18px' }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.1em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:8 }}>{label}</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:24, fontWeight:700, color:'var(--page-text)', lineHeight:1, marginBottom:6 }}>{value}</div>
      {sub && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: subColor || 'var(--page-muted)' }}>{sub}</div>}
    </div>
  );
}

function DataRow({ label, value, sub, subColor, onClick, rank }) {
  return (
    <div
      onClick={onClick}
      style={{
        display:'flex', alignItems:'flex-start', gap:8, padding:'10px 12px',
        borderBottom:'1px solid var(--card-border)',
        cursor: onClick ? 'pointer' : 'default',
        transition:'background 0.15s',
        minWidth:0, overflow:'hidden',
      }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background='var(--page)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background='')}
    >
      {rank != null && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', width:20, flexShrink:0, paddingTop:1 }}>{String(rank+1).padStart(2,'0')}</div>}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:12, color:'var(--page-text)', wordBreak:'break-word', lineHeight:'1.4' }}>{label}</div>
        {sub && !subColor && <div style={{ fontSize:10, color:'var(--page-muted)', marginTop:2 }}>{sub}</div>}
      </div>
      <div style={{ textAlign:'right', flexShrink:0, paddingTop:1 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, color:'var(--page-text)', whiteSpace:'nowrap' }}>{value}</div>
        {subColor && <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:subColor, whiteSpace:'nowrap' }}>{sub}</div>}
      </div>
      {onClick && <div style={{ color:'var(--page-muted)', fontSize:12, flexShrink:0, paddingTop:1 }}>›</div>}
    </div>
  );
}

// ── Breadcrumb ────────────────────────────────────────────────
function Breadcrumb({ crumbs, onNavigate }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:24, flexWrap:'wrap' }}>
      {crumbs.map((c, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span style={{ color:'var(--page-muted)', fontSize:12 }}>›</span>}
          <span
            onClick={() => i < crumbs.length - 1 && onNavigate(i)}
            style={{
              fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em',
              textTransform:'uppercase', color: i === crumbs.length-1 ? 'var(--gold)' : 'var(--page-muted)',
              cursor: i < crumbs.length-1 ? 'pointer' : 'default',
              textDecoration: i < crumbs.length-1 ? 'underline' : 'none',
            }}
          >{c.label}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Custom tooltip for charts ─────────────────────────────────
function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background:'var(--void)', border:'1px solid var(--card-border)', padding:'10px 14px', borderRadius:3 }}>
      <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', marginBottom:6 }}>{label}</div>
      {payload.map((p,i) => (
        <div key={i} style={{ fontSize:12, color:p.color }}>{p.name}: {fmtR(p.value)}</div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 0 — Overview
// ════════════════════════════════════════════════════════════
function SalesOverview({ period = 'MTD', onDrillMonth, onDrillBranch, onDrillDept, onDrillProduct }) {
  const isMobile = useIsMobile();
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);

  // Helper shorthand for filters
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;

  const kpiDax = `EVALUATE ROW(
    "TotalSales",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
    "LastYrSales", CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
    "TotalQty",    CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
    "TotalPur",    CALCULATE(SUM('Transactions'[PURCHASES]),  ${cyF})
  )`;

  const trendDax = `EVALUATE
    ADDCOLUMNS(
      FILTER(
        SUMMARIZE('Transactions', 'Date'[YYYYMM], 'Date'[MonthName], 'Date'[Year], 'Date'[MonthNo]),
        'Date'[YYYYMM] >= 202404
      ),
      "Revenue",   CALCULATE(SUM('Transactions'[SALES])),
      "Purchases", CALCULATE(SUM('Transactions'[PURCHASES]))
    )
    ORDER BY 'Date'[YYYYMM] ASC`;

  const branchDax = `EVALUATE
    TOPN(8,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'CompanyList'[COMPANY NAME]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
        "Growth",   DIVIDE(
          CALCULATE(SUM('Transactions'[SALES]), ${cyF}) - CALCULATE(SUM('Transactions'[SALES]), ${pyF}),
          CALCULATE(SUM('Transactions'[SALES]), ${pyF})
        )
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const deptDax = `EVALUATE
    TOPN(8,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'Departments'[DeptName]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]), ${cyF}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]), ${pyF})
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const prodDax = `EVALUATE
    TOPN(8,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'Transactions'[CODE], 'Stock'[Description]
        ),
        "Revenue", CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
        "Qty",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}),
        "OnHand",  MAX('Stock'[ONHAND])
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const { data: kpi,    loading: l0, error: e0, reload: r0 } = usePbi(kpiDax);
  const { data: trend,  loading: l1, error: e1, reload: r1 } = usePbi(trendDax);
  const { data: branch, loading: l2, error: e2, reload: r2 } = usePbi(branchDax);
  const { data: dept,   loading: l3, error: e3, reload: r3 } = usePbi(deptDax);
  const { data: prod,   loading: l4, error: e4, reload: r4 } = usePbi(prodDax);

  const kpiRow = Array.isArray(kpi) ? kpi[0] : kpi;
  const totalSales = kpiRow?.['[TotalSales]'];
  const lastYr     = kpiRow?.['[LastYrSales]'];
  const growth     = totalSales && lastYr ? (totalSales - lastYr) / lastYr : null;
  const totalQty   = kpiRow?.['[TotalQty]'];
  const totalPur   = kpiRow?.['[TotalPur]'];
  const gp         = totalSales && totalPur ? (totalSales - totalPur) / totalSales : null;

  const trendData = (trend || [])
    .filter(r => r['[Revenue]'] != null)
    .slice(-12)
    .map(r => ({
      label: `${r['Date[MonthName]']?.slice(0,3)} ${String(r['Date[Year]']).slice(2)}`,
      yyyymm: r['Date[YYYYMM]'],
      Revenue: r['[Revenue]'],
      Purchases: r['[Purchases]'],
    }));

  const maxRevenue = Math.max(...(branch || []).map(r => r['[Revenue]'] || 0));

  return (
    <div style={{ minWidth:0, overflowX:'hidden' }}>
      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.18em', textTransform:'uppercase', color:'var(--page-muted)' }}>Sales & Revenue</div>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.08em', padding:'3px 10px', background:'rgba(26,173,99,0.1)', color:'var(--green-l)', borderRadius:2 }}>LIVE</div>
      </div>

      {/* KPI strip */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(4, minmax(0,1fr))', gap:10, marginBottom:16 }}>
        {l0 ? [0,1,2,3].map(i => <div key={i} style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, height:82 }}><Spinner /></div>)
          : e0 ? <div style={{ gridColumn:'1/-1' }}><ErrorBox msg={e0} onRetry={r0}/></div>
          : <>
          <KpiCard label="Total Revenue" value={fmtR(totalSales)}
            sub={`${fmtPct(growth)} vs last year`}
            subColor={growth < 0 ? RED : GREEN} />
          <KpiCard label="Gross Margin" value={gp != null ? `${(gp*100).toFixed(1)}%` : '—'}
            sub="excl. overhead" subColor="var(--page-muted)" />
          <KpiCard label="Units Sold" value={fmtN(totalQty)}
            sub="current period" subColor="var(--page-muted)" />
          <KpiCard label="Last Year Revenue" value={fmtR(lastYr)}
            sub="same period" subColor="var(--page-muted)" />
        </>}
      </div>

      {/* Trend + Branch */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.6fr) minmax(0,1fr)', gap:12, marginBottom:12 }}>
        <Card>
          <CardHeader title="Revenue vs Purchases — Monthly Trend" />
          <div style={{ padding:'0 20px 20px' }}>
            {l1 ? <Spinner /> : e1 ? <ErrorBox msg={e1} onRetry={r1}/> : trendData.length === 0 ? <div style={{ padding:24, textAlign:'center', color:'var(--page-muted)', fontFamily:'var(--font-mono)', fontSize:11 }}>No trend data</div> : (
              <>
                <div style={{ display:'flex', gap:16, marginBottom:12 }}>
                  {[['Revenue', GOLD], ['Purchases', GREEN]].map(([k,c]) => (
                    <div key={k} style={{ display:'flex', alignItems:'center', gap:5 }}>
                      <div style={{ width:8, height:8, borderRadius:'50%', background:c }}/>
                      <span style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)' }}>{k}</span>
                    </div>
                  ))}
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={trendData} barGap={2}
                    onClick={(d) => d?.activePayload && onDrillMonth(d.activePayload[0].payload)}>
                    <XAxis dataKey="label" tick={{ fontFamily:'var(--font-mono)', fontSize:9, fill:'var(--page-muted)' }} axisLine={false} tickLine={false}/>
                    <YAxis hide />
                    <Tooltip content={<ChartTip/>} />
                    <Bar dataKey="Revenue" fill={GOLD} radius={[2,2,0,0]} cursor="pointer" name="Revenue"/>
                    <Bar dataKey="Purchases" fill={GREEN} radius={[2,2,0,0]} cursor="pointer" name="Purchases"/>
                  </BarChart>
                </ResponsiveContainer>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', marginTop:8 }}>Click a bar to drill into that month</div>
              </>
            )}
          </div>
        </Card>

        <Card>
          <CardHeader title="Branch Performance" subtitle="Click to drill down" />
          {l2 ? <Spinner /> : e2 ? <ErrorBox msg={e2} onRetry={r2}/> : (branch || []).map((r, i) => {
            const rev  = r['[Revenue]'];
            const grow = r['[Growth]'];
            const barW = maxRevenue > 0 ? Math.round((rev / maxRevenue) * 100) : 0;            return (
              <div key={i} onClick={() => onDrillBranch(r['CompanyList[COMPANY NAME]'])}
                style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'9px 12px', borderBottom:'1px solid var(--card-border)', cursor:'pointer', transition:'background 0.15s', minWidth:0, overflow:'hidden' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--page)'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', width:18, flexShrink:0, paddingTop:1 }}>{String(i+1).padStart(2,'0')}</div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:12, color:'var(--page-text)', wordBreak:'break-word', lineHeight:'1.4' }}>
                    {r['CompanyList[COMPANY NAME]']}
                  </div>
                  <div style={{ height:3, background:'var(--card-border)', borderRadius:2, marginTop:4 }}>
                    <div style={{ height:3, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                  </div>
                </div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, color:'var(--page-text)', width:60, textAlign:'right', flexShrink:0 }}>{fmtR(rev)}</div>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: grow < 0 ? RED : GREEN, width:42, textAlign:'right', flexShrink:0 }}>{fmtPct(grow)}</div>
                <div style={{ color:'var(--page-muted)', fontSize:12 }}>›</div>
              </div>
            );
          })}
        </Card>
      </div>

      {/* Dept + Products */}
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap:12 }}>
        <Card>
          <CardHeader title="Top Departments" subtitle="Click to drill down" />
          {l3 ? <Spinner /> : e3 ? <ErrorBox msg={e3} onRetry={r3}/> : (dept || []).map((r, i) => {
            const rev  = r['[Revenue]'];
            const ly   = r['[LastYear]'];
            const grow = ly ? (rev - ly) / ly : null;
            return (
              <DataRow key={i} rank={i}
                label={r['Departments[DeptName]']}
                value={fmtR(rev)}
                sub={grow != null ? fmtPct(grow) : ''}
                subColor={grow != null ? (grow < 0 ? RED : GREEN) : undefined}
                onClick={() => onDrillDept(r['Departments[DeptName]'])} />
            );
          })}
        </Card>

        <Card>
          <CardHeader title="Top Products" subtitle="Click to drill down" />
          {l4 ? <Spinner /> : e4 ? <ErrorBox msg={e4} onRetry={r4}/> : (prod || []).map((r, i) => (
            <DataRow key={i} rank={i}
              label={r['Stock[Description]'] || r['Transactions[CODE]']}
              value={fmtR(r['[Revenue]'])}
              sub={`${fmtN(r['[Qty]'])} units`}
              onClick={() => onDrillProduct(r['Transactions[CODE]'], r['Stock[Description]'])} />
          ))}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 1 — Month Detail
// ════════════════════════════════════════════════════════════
function SalesMonthDetail({ month, period = 'MTD', onDrillBranch }) {
  const yyyymm = month?.yyyymm;
  // Use the same period date range as the rest of the page
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;

  const dax = `EVALUATE
    TOPN(20,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'CompanyList'[COMPANY NAME]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${pyF}),
        "Growth",   DIVIDE(
          CALCULATE(SUM('Transactions'[SALES]), ${cyF}) - CALCULATE(SUM('Transactions'[SALES]), ${pyF}),
          CALCULATE(SUM('Transactions'[SALES]), ${pyF})
        ),
        "Qty",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF})
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const { data, loading, error, reload } = usePbi(dax);
  const maxRev = Math.max(...(data || []).map(r => r['[Revenue]'] || 0));

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'minmax(0,1fr)', gap:12 }}>
        <Card>
          <CardHeader title={`Branch breakdown — ${period}`} subtitle="Click a branch to drill down" />
          {loading ? <Spinner /> : error ? <ErrorBox msg={error} onRetry={reload}/> : (data || []).map((r, i) => {
            const rev  = r['[Revenue]'];
            const grow = r['[Growth]'];
            const barW = maxRev > 0 ? Math.round((rev / maxRev) * 100) : 0;
            return (
              <div key={i} onClick={() => onDrillBranch(r['CompanyList[COMPANY NAME]'], yyyymm)}
                style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'10px 12px', borderBottom:'1px solid var(--card-border)', cursor:'pointer', transition:'background 0.15s', minWidth:0, overflow:'hidden' }}
                onMouseEnter={e => e.currentTarget.style.background='var(--page)'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color:'var(--page-muted)', width:18, flexShrink:0, paddingTop:1 }}>{String(i+1).padStart(2,'0')}</div>
                <div style={{ flex:1, minWidth:0, overflow:'hidden' }}>
                  <div style={{ fontSize:12, color:'var(--page-text)', wordBreak:'break-word', lineHeight:'1.4' }}>
                    {r['CompanyList[COMPANY NAME]']}
                  </div>
                  <div style={{ height:3, background:'var(--card-border)', borderRadius:2, marginTop:4 }}>
                    <div style={{ height:3, width:`${barW}%`, background:GOLD, borderRadius:2 }}/>
                  </div>
                </div>
                <div style={{ textAlign:'right', flexShrink:0, minWidth:52 }}>
                  <div style={{ fontFamily:'var(--font-display)', fontSize:13, fontWeight:600, color:'var(--page-text)', whiteSpace:'nowrap' }}>{fmtR(rev)}</div>
                  <div style={{ fontFamily:'var(--font-mono)', fontSize:10, color: grow < 0 ? RED : GREEN, whiteSpace:'nowrap' }}>{fmtPct(grow)}</div>
                </div>
                <div style={{ color:'var(--page-muted)', fontSize:12, flexShrink:0 }}>›</div>
              </div>
            );
          })}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 2 — Branch Detail
// ════════════════════════════════════════════════════════════
function SalesBranchDetail({ branch, yyyymm, period = 'MTD', onDrillDept }) {
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;
  const branchCalc = `'CompanyList'[COMPANY NAME] = "${branch}"`;

  const deptDax = `EVALUATE
    TOPN(15,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'Departments'[DeptName]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}, ${branchCalc}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${pyF}, ${branchCalc}),
        "Qty",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}, ${branchCalc})
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const prodDax = `EVALUATE
    TOPN(10,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'Transactions'[CODE], 'Stock'[Description]
        ),
        "Revenue", CALCULATE(SUM('Transactions'[SALES]),      ${cyF}, ${branchCalc}),
        "Qty",     CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}, ${branchCalc})
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const { data: depts, loading: l1, error: e1, reload: r1 } = usePbi(deptDax);
  const { data: prods, loading: l2, error: e2, reload: r2 } = usePbi(prodDax);
  const isMobile = useIsMobile();

  return (
    <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap:12, minWidth:0, overflowX:'hidden' }}>
      <Card>
        <CardHeader title="Top Departments" subtitle="Click to drill down" />
        {l1 ? <Spinner /> : e1 ? <ErrorBox msg={e1} onRetry={r1}/> : (depts || []).map((r, i) => {
          const rev = r['[Revenue]'];
          const ly  = r['[LastYear]'];
          const grow = ly ? (rev - ly) / ly : null;
          return (
            <DataRow key={i} rank={i}
              label={r['Departments[DeptName]']}
              value={fmtR(rev)}
              sub={grow != null ? fmtPct(grow) : ''}
              subColor={grow != null ? (grow < 0 ? RED : GREEN) : undefined}
              onClick={() => onDrillDept(r['Departments[DeptName]'], branch)} />
          );
        })}
      </Card>
      <Card>
        <CardHeader title="Top Products" />
        {l2 ? <Spinner /> : e2 ? <ErrorBox msg={e2} onRetry={r2}/> : (prods || []).map((r, i) => (
          <DataRow key={i} rank={i}
            label={r['Stock[Description]'] || r['Transactions[CODE]']}
            value={fmtR(r['[Revenue]'])}
            sub={`${fmtN(r['[Qty]'])} units`} />
        ))}
      </Card>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 3 — Department Detail
// ════════════════════════════════════════════════════════════
function SalesDeptDetail({ dept, branch, period = 'MTD', onDrillProduct }) {
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF    = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF    = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;
  const deptCalc   = `'Departments'[DeptName] = "${dept}"`;
  const branchCalc = branch ? `, 'CompanyList'[COMPANY NAME] = "${branch}"` : '';

  const dax = `EVALUATE
    TOPN(20,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${cyF}),
          'Transactions'[CODE], 'Stock'[Description]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${cyF}, ${deptCalc}${branchCalc}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${pyF}, ${deptCalc}${branchCalc}),
        "Qty",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${cyF}, ${deptCalc}${branchCalc}),
        "OnHand",   MAX('Stock'[ONHAND])
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  const { data, loading, error, reload } = usePbi(dax);

  return (
    <Card>
      <CardHeader title={`Products in ${dept}`} subtitle={branch ? `Filtered to ${branch}` : 'All branches'} />
      {loading ? <Spinner /> : error ? <ErrorBox msg={error} onRetry={reload}/> : (data || []).map((r, i) => (
        <DataRow key={i} rank={i}
          label={r['Stock[Description]'] || r['Transactions[CODE]']}
          value={fmtR(r['[Revenue]'])}
          sub={`${fmtN(r['[Qty]'])} units sold`}
          onClick={() => onDrillProduct(r['Transactions[CODE]'], r['Stock[Description]'])} />
      ))}
    </Card>
  );
}

// ════════════════════════════════════════════════════════════
// LEVEL 4 — Product Detail
// ════════════════════════════════════════════════════════════
function SalesProductDetail({ code, name, period = 'MTD' }) {
  const { startDate, endDate, pyStart, pyEnd } = getDateRange(period);
  const cyF  = `'Transactions'[TXDATE] >= DATE(${startDate}) && 'Transactions'[TXDATE] <= DATE(${endDate})`;
  const pyF  = `'Transactions'[TXDATE] >= DATE(${pyStart})  && 'Transactions'[TXDATE] <= DATE(${pyEnd})`;
  const codeF = `'Transactions'[CODE] = "${code}"`;

  const branchDax = `EVALUATE
    TOPN(20,
      ADDCOLUMNS(
        SUMMARIZE(
          FILTER('Transactions', ${codeF} && ${cyF}),
          'CompanyList'[COMPANY NAME]
        ),
        "Revenue",  CALCULATE(SUM('Transactions'[SALES]),      ${codeF}, ${cyF}),
        "LastYear", CALCULATE(SUM('Transactions'[SALES]),      ${codeF}, ${pyF}),
        "Qty",      CALCULATE(SUM('Transactions'[UNITS_SOLD]), ${codeF}, ${cyF})
      ),
      [Revenue], DESC
    )
    ORDER BY [Revenue] DESC`;

  // Trend: always show full history regardless of period (context for the product)
  // but branch breakdown respects the period filter
  const trendDax = `EVALUATE
    ADDCOLUMNS(
      FILTER(
        SUMMARIZE(
          FILTER('Transactions', ${codeF}),
          'Date'[YYYYMM], 'Date'[MonthName], 'Date'[Year]
        ),
        'Date'[YYYYMM] >= 202404
      ),
      "Revenue",   CALCULATE(SUM('Transactions'[SALES]),      ${codeF}),
      "Purchases", CALCULATE(SUM('Transactions'[PURCHASES]),  ${codeF})
    )
    ORDER BY 'Date'[YYYYMM] ASC`;

  const stockDax = `EVALUATE
    ADDCOLUMNS(
      SELECTCOLUMNS(
        FILTER('Stock', 'Stock'[StockCode] = "${code}"),
        "Code",        'Stock'[StockCode],
        "Description", 'Stock'[Description],
        "SellPrice",   'Stock'[SELLPRICE1],
        "AvgCost",     'Stock'[AVRGCOST]
      ),
      "OnHand",   CALCULATE(SUM('StockBalances'[ONHAND]),  FILTER('StockBalances','StockBalances'[CODE]="${code}")),
      "Laybye",   CALCULATE(SUM('StockBalances'[LAYBYE]),  FILTER('StockBalances','StockBalances'[CODE]="${code}")),
      "StockVal", CALCULATE(SUMX(FILTER('StockBalances','StockBalances'[CODE]="${code}"&&'StockBalances'[ONHAND]>0),'StockBalances'[ONHAND]*RELATED('Stock'[AVRGCOST])))
    )`;

  const { data: branches, loading: l1, error: e1, reload: r1 } = usePbi(branchDax);
  const { data: trend,    loading: l2, error: e2, reload: r2 } = usePbi(trendDax);
  const { data: stock,    loading: l3, error: e3, reload: r3 } = usePbi(stockDax);

  const stockRow = Array.isArray(stock) ? stock[0] : null;
  const trendData = (trend || [])
    .filter(r => r['[Revenue]'] != null)
    .map(r => ({
      label: `${r['Date[MonthName]']?.slice(0,3)} ${String(r['Date[Year]']).slice(2)}`,
      Revenue: r['[Revenue]'],
    }));
  const isMobile = useIsMobile();

  return (
    <div>
      {/* Product header */}
      <div style={{ background:'var(--card)', border:'1px solid var(--card-border)', borderRadius:4, padding:'20px 24px', marginBottom:12 }}>
        <div style={{ fontFamily:'var(--font-mono)', fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--page-muted)', marginBottom:6 }}>Product Code: {code}</div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:20, fontWeight:700, color:'var(--page-text)', marginBottom:12 }}>{name || code}</div>
        {l3 ? <Spinner /> : e3 ? <ErrorBox msg={e3} onRetry={r3}/> : stockRow && (
          <div style={{ display:'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(5, 1fr)', gap:12 }}>
            {[
              ['On Hand',    fmtN(stockRow['[OnHand]'])],
              ['On Laybye',  fmtN(stockRow['[Laybye]'])],
              ['Stock Value',fmtR(stockRow['[StockVal]'])],
              ['Sell Price', fmtR(stockRow['[SellPrice]'])],
              ['Avg Cost',   fmtR(stockRow['[AvgCost]'])],
            ].map(([l,v]) => (
              <div key={l}>
                <div style={{ fontFamily:'var(--font-mono)', fontSize:9, color:'var(--page-muted)', letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:4 }}>{l}</div>
                <div style={{ fontFamily:'var(--font-display)', fontSize:18, fontWeight:600, color:'var(--page-text)' }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.4fr) minmax(0,1fr)', gap:12 }}>
        {/* Monthly trend */}
        <Card>
          <CardHeader title="Monthly Revenue Trend" />
          <div style={{ padding:'0 20px 20px' }}>
            {l2 ? <Spinner /> : e2 ? <ErrorBox msg={e2} onRetry={r2}/> : (
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={trendData}>
                  <XAxis dataKey="label" tick={{ fontFamily:'var(--font-mono)', fontSize:9, fill:'var(--page-muted)' }} axisLine={false} tickLine={false}/>
                  <YAxis hide />
                  <Tooltip content={<ChartTip/>} />
                  <Line type="monotone" dataKey="Revenue" stroke={GOLD} strokeWidth={2} dot={false} name="Revenue"/>
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        {/* Branch breakdown */}
        <Card>
          <CardHeader title={`Sales by Branch — ${period}`} />
          {l1 ? <Spinner /> : e1 ? <ErrorBox msg={e1} onRetry={r1}/> : (branches || [])
            .filter(r => r['[Revenue]'] > 0)
            .map((r, i) => (
              <DataRow key={i} rank={i}
                label={r['CompanyList[COMPANY NAME]']}
                value={fmtR(r['[Revenue]'])}
                sub={`${fmtN(r['[Qty]'])} units`} />
            ))}
        </Card>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// MAIN — Sales Page (manages navigation state)
// ════════════════════════════════════════════════════════════
export default function SalesPage() {
  const { period = 'MTD' } = useOutletContext() || {};
  const isMobile = useIsMobile();
  const [nav, setNav] = useState([{ level: 0, label: 'Sales' }]);

  // Do NOT reset nav when period changes — re-query in place

  const current = nav[nav.length - 1];

  const navigateTo = (crumb) => setNav(prev => [...prev, crumb]);
  const navigateBack = (idx) => setNav(prev => prev.slice(0, idx + 1));

  const crumbs = nav.map((n) => ({ label: n.label }));

  return (
    <div style={{ animation:'fadeUp 0.4s ease both', minWidth:0, overflowX:'hidden' }}>
      <Breadcrumb crumbs={crumbs} onNavigate={navigateBack} />

      {current.level === 0 && (
        <SalesOverview
          period={period}
          onDrillMonth={(row) => navigateTo({ level:1, label: row.label, yyyymm: row.yyyymm })}
          onDrillBranch={(branch) => navigateTo({ level:2, label: branch, branch })}
          onDrillDept={(dept) => navigateTo({ level:3, label: dept, dept, branch: null })}
          onDrillProduct={(code, name) => navigateTo({ level:4, label: name || code, code, name })}
        />
      )}

      {current.level === 1 && (
        <SalesMonthDetail
          key={period}
          month={{ label: current.label, yyyymm: current.yyyymm }}
          period={period}
          onDrillBranch={(branch, yyyymm) => navigateTo({ level:2, label: branch, branch, yyyymm })}
        />
      )}

      {current.level === 2 && (
        <SalesBranchDetail
          key={period}
          branch={current.branch}
          yyyymm={current.yyyymm}
          period={period}
          onDrillDept={(dept, branch) => navigateTo({ level:3, label: dept, dept, branch })}
        />
      )}

      {current.level === 3 && (
        <SalesDeptDetail
          key={period}
          dept={current.dept}
          branch={current.branch}
          period={period}
          onDrillProduct={(code, name) => navigateTo({ level:4, label: name || code, code, name })}
        />
      )}

      {current.level === 4 && (
        <SalesProductDetail key={period} code={current.code} name={current.name} period={period} />
      )}
    </div>
  );
}
